import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {ClerkService} from 'ngx-clerk';
import {Observable, Subscription} from 'rxjs';
import {filter, tap, map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class SessionService {
  private subscriptions = new Subscription();

  constructor(private clerk: ClerkService, private router: Router) {
    this.setupSessionListeners();
  }

  /**
   * Setup listeners for session changes
   */
  private setupSessionListeners(): void {
    // Listen for user authentication changes
    this.subscriptions.add(
      this.clerk.user$
        .pipe(
          filter(user => user !== undefined), // Wait for initial load
          tap(user => {
            if (user) {
              // User just logged in - handle session
              this.handleLogin();
            } else {
              // User logged out - handle session
              this.handleLogout();
            }
          })
        )
        .subscribe()
    );

    // Listen for session changes
    this.subscriptions.add(
      this.clerk.session$
        .pipe(
          filter(session => session !== undefined),
          tap(session => {
            if (session) {
              console.log('Session active:', session.id);
              // Session is active, you can store session token if needed
              this.storeSessionToken(session);
            } else {
              console.log('Session ended');
              this.clearSessionToken();
            }
          })
        )
        .subscribe()
    );
  }

  /**
   * Handle user login - redirect to main app
   */
  private handleLogin(): void {
    // Get current URL to redirect back after login
    const currentUrl = this.router.url;

    // If user is on landing page, redirect to main app
    if (currentUrl.includes('/landing') || currentUrl === '/') {
      this.router.navigate(['/'], {replaceUrl: true});
    }
  }

  /**
   * Handle user logout - redirect to landing
   */
  private handleLogout(): void {
    // Clear any local session data
    this.clearSessionToken();

    // Redirect to landing page
    this.router.navigate(['/landing'], {replaceUrl: true});
  }

  /**
   * Get current session token (for API calls)
   */
  async getSessionToken(): Promise<string | null> {
    try {
      const session = await this.clerk.session$.pipe(filter(s => s !== undefined)).toPromise();
      if (session) {
        // Clerk automatically handles token refresh
        return session.getToken();
      }
      return null;
    } catch (error) {
      console.error('Error getting session token:', error);
      return null;
    }
  }

  /**
   * Store session token in localStorage (optional, for offline access)
   */
  private storeSessionToken(session: any): void {
    // Clerk handles session persistence automatically
    // This is just for reference if you need to store additional data
    try {
      if (session?.id) {
        localStorage.setItem('clerk_session_id', session.id);
      }
    } catch (error) {
      console.warn('Could not store session token:', error);
    }
  }

  /**
   * Clear session token from localStorage
   */
  private clearSessionToken(): void {
    try {
      localStorage.removeItem('clerk_session_id');
    } catch (error) {
      console.warn('Could not clear session token:', error);
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      const clerk = await this.clerk.clerk$.pipe(filter(c => c !== null)).toPromise();
      if (clerk) {
        await clerk.signOut();
        // handleLogout will be called automatically via listener
      }
    } catch (error) {
      console.error('Error during logout:', error);
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): Observable<boolean> {
    return this.clerk.user$.pipe(
      filter(user => user !== undefined),
      map(user => !!user)
    );
  }

  /**
   * Get current user
   */
  getCurrentUser(): Observable<any> {
    return this.clerk.user$;
  }

  /**
   * Get current session
   */
  getCurrentSession(): Observable<any> {
    return this.clerk.session$;
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
